import { getActualTimeRate, getRate } from '@/apis/pay'
import { getUserInfo, setUserInfo } from '@/apis/user'
import { ElMessage } from 'element-plus'
import { defineStore } from 'pinia'
import { USER_STORE_KEY } from './config'

import { getLang } from '@/utils/getLanguage'
export const unitMap = {
  CNY: '￥', // 人民币
  USD: '$', // 美元
  EUR: '€', // 欧元
  PLN: 'zł', // 波兰
  // RUB: '₽', // 俄罗斯
  SGD: 'S$', // 新加坡
  GBP: '£', // 英镑
  CAD: 'C$', // 加拿大
  AUD: 'A$', // 澳大利亚
  BRL: 'R$', // 巴西
  DKK: 'kr', // 丹麦
}

const { language, currency } = getLang()
const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches
export default defineStore('userStore', {
  state: () => {
    return {
      currency, // 币种
      language, // 语言
      token: undefined,
      defaultEmail: '', // 登录页回显邮箱
      profile: {},
      redirectUrl: '/',
      rate: 1,
      currencyRate: 1,
      loginTimer: undefined,
      transferFromMenu: 'pecenter',
      rateLoading: false,
      agreeTransport: false, // 初次进页面协议
      agreeTransport2: false, // 转运确认协议
      notShowBrazilDeclare: false, // 不再显示巴西申报协议
      notShowRehearsalRecordDeclare: false, // 不再显示预演记录协议
      homeVideoVisible: true, // 首页视频是否显示
      channel: '', // 渠道
      isSystemTheme: true,
      themeMode: isDark ? 'dark' : 'light',
      showTopAd: true,
    }
  },

  //计算总价 index.js 用store调用就行
  getters: {
    isLogin: (state) => state.token && state.profile.id,
    currencyUnit: (state) => unitMap[state.currency],
  },

  //同步异步都可以
  actions: {
    async login(res) {
      this.token = (res.tokenHead || '') + res.token
      this.loginTimer = Date.now()
      await this.getUserInfo()
      this.showTopAd = true
    },
    // 获取登录用户信息
    async getUserInfo() {
      try {
        const profile = await getUserInfo()
        this.defaultEmail = profile.email
        this.profile = { ...this.profile, ...profile, id: profile.userId }
      } catch (error) {
        this.token = ''
      }
    },
    // 获取登录用户信息
    async setUserInfo(params) {
      await setUserInfo(params)
      this.getUserInfo()
    },
    // 退出登录
    logout() {
      this.token = undefined
      this.loginTimer = undefined
      this.channel = undefined
      this.profile = {}
    },
    // 选择币种和语言
    async chooseLangAndCurrency(lang, currency) {
      if (lang) {
        this.language = lang
      }
      if (currency) {
        await this.getRate(currency)
        this.currency = currency
      }
    },
    // 修改回跳地址
    setRedirectUrl(url) {
      this.redirectUrl = url
    },
    // 更改所选语言和币种
    async setSelectLangAndCurrency(lang, currency) {
      try {
        await this.chooseLangAndCurrency(lang, currency)
      } catch (error) {
        ElMessage({ type: 'error', message: error })
      }
    },
    // 设置服务器实时汇率(非网站汇率)
    async setSelectCurrencyRate(currency) {
      if (currency) {
        try {
          this.rateLoading = true
          const currencyRate = await getActualTimeRate({
            currency: currency || this.currency,
          })
          this.currencyRate = currencyRate
          console.log('this.currencyRate :', this.currencyRate)
        } catch (error) {
          console.log(error)
        } finally {
          setTimeout(() => {
            this.rateLoading = false
          }, 10)
        }
      }
    },
    // 获取汇率
    async getRate(currency, isDefault = false) {
      try {
        if (isDefault) {
          await this.setDefaultCurrency()
        }
        this.rateLoading = true
        const rate = await getRate({ currency: currency || this.currency })
        this.rate = rate
        this.getCurrencyRate()
      } catch (error) {
        console.log(error)
      } finally {
        setTimeout(() => {
          this.rateLoading = false
        }, 10)
      }
    },
    // 获取服务器实时汇率(非网站汇率)
    async getCurrencyRate(currency) {
      const currencyRate = await getActualTimeRate({
        currency: currency || this.currency,
      })
      this.currencyRate = currencyRate
    },
    // 设置免责声明
    setDisclaimerAgree(isAgree, noShow) {
      this.profile.disclaimerAgree = isAgree
      this.profile.againShowAgree = noShow ?? this.profile.againShowAgree
    },
    // 同意 分销
    agreeDistribution() {
      this.profile.isAgreeDistribution = true
    },
    // 同意 提包协议
    agreeSubmitPack(isAgree) {
      this.profile.isAgreeSubmitPack = isAgree
    },
    // 设置银行转帐来自哪里
    setTransferFromMenu(transferFromMenu) {
      this.transferFromMenu = transferFromMenu
    },
    // 同意转运协议
    agreeTransportAgreement(type = 1, isAgree = true) {
      if (type === 1) {
        this.agreeTransport = isAgree
      } else {
        this.agreeTransport2 = isAgree
      }
    },
    // 不再显示巴西申报协议
    setNotShowBrazilDeclare(isNotShow) {
      this.notShowBrazilDeclare = isNotShow
    },
    // 不再显示预演记录协议
    setNotShowRehearsalRecordDeclare(isNotShow) {
      this.notShowRehearsalRecordDeclare = isNotShow
    },
    // 设置首页视频是否显示
    setHomeVideoVisible(isVisible) {
      this.homeVideoVisible = isVisible
    },
    // 设置渠道
    setChannel(channel) {
      this.channel = channel
    },
    setThemeMode(mode, isSystemTheme) {
      this.themeMode = mode
      this.isSystemTheme = isSystemTheme
      document.documentElement.classList.add(mode)
      document.documentElement.classList.remove(mode === 'dark' ? 'light' : 'dark')
      if (mode === 'dark') {
        document.documentElement.setAttribute('theme-mode', 'dark')
      } else {
        document.documentElement.setAttribute('theme-mode', 'light')
      }
    },
    async setDefaultCurrency() {
      this.currency = this.profile?.countryCode === 'PL' ? 'PLN' : this.currency
    },
    // 不显示顶部广告
    setShowTopAd(isShow) {
      this.showTopAd = isShow
    },
  },
  persist: {
    key: USER_STORE_KEY,
    storage: localStorage,
    paths: [
      'token',
      'profile',
      'defaultEmail',
      'language',
      'currency',
      'rate',
      'loginTimer',
      'agreeTransport',
      'agreeTransport2',
      'notShowBrazilDeclare',
      'channel',
      'homeVideoVisible',
      'currencyRate',
      'themeMode',
      'isSystemTheme',
      'showTopAd',
    ],
  },
})
